/**
 * Phase 8.1 — Outbound Report (Reporte de Salidas).
 *
 * Single tab inside /inventory that visualises every outbound movement
 * (stock_delta < 0). One API call to `/api/inventory/outbound-report` returns
 * the entire payload (KPIs, 3 rankings, by_vendor, trend, pareto,
 * period-vs-previous comparison). Charts are rendered with recharts.
 *
 * Reads only — no mutations to product_movements / transactions.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import {
  TrendingDown,
  TrendingUp,
  Package,
  DollarSign,
  Hash,
  ListChecks,
  Award,
  ArrowDown,
  ArrowUp,
  Minus,
  Loader2,
  Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import inventory from "@/api/inventory";

const COLORS = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#ef4444", // red-500
  "#a855f7", // purple-500
  "#06b6d4", // cyan-500
  "#84cc16", // lime-500
  "#ec4899", // pink-500
  "#f97316", // orange-500
  "#6366f1", // indigo-500
  "#71717a", // zinc-500 (Otros)
];

const fmt = (n, digits = 0) =>
  typeof n === "number"
    ? n.toLocaleString("es-MX", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : n ?? "—";

const fmtMoney = (n) => (typeof n === "number" ? `$${fmt(n, 2)}` : "—");

const fmtPct = (n) => (typeof n === "number" ? `${n.toFixed(2)}%` : "—");

const fmtTrendDate = (s, granularity) => {
  if (!s) return "";
  if (granularity === "month") return s; // YYYY-MM
  if (granularity === "week") {
    // YYYY-WW → label as "Sem N"
    const parts = s.split("-");
    return `S${parts[1]}/${parts[0].slice(2)}`;
  }
  // day YYYY-MM-DD → DD/MM
  const [, m, d] = s.split("-");
  return `${d}/${m}`;
};

/** KPI card with icon + value + optional sub-label. */
function KpiCard({ icon: Icon, label, value, subLabel, testId, accent }) {
  const accentClass =
    accent === "primary"
      ? "text-primary"
      : accent === "danger"
      ? "text-red-500 dark:text-red-400"
      : "text-muted-foreground";
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="mt-1 font-mono text-xl font-semibold tabular-nums break-all">
              {value}
            </div>
            {subLabel && (
              <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                {subLabel}
              </div>
            )}
          </div>
          <Icon className={`h-4 w-4 shrink-0 ${accentClass}`} />
        </div>
      </CardContent>
    </Card>
  );
}

/** Comparative period card: shows current/previous with delta arrow. */
function PeriodCard({ title, stats, deltaPct, isDelta, t }) {
  const arrow = (v) => {
    if (v === null || v === undefined) return <Minus className="h-3 w-3" />;
    if (v > 0) return <ArrowUp className="h-3 w-3" />;
    if (v < 0) return <ArrowDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };
  const tone = (v) => {
    if (v === null || v === undefined || v === 0) return "text-muted-foreground";
    return v > 0 ? "text-emerald-500 dark:text-emerald-400" : "text-rose-500 dark:text-rose-400";
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-1.5">
        <Row
          label={t("inventory.outboundReport.units", { defaultValue: "Unidades" })}
          value={isDelta ? fmtPct(stats?.qty) : fmt(stats?.total_qty)}
          delta={isDelta}
          tone={isDelta ? tone(stats?.qty) : null}
          icon={isDelta ? arrow(stats?.qty) : null}
        />
        <Row
          label={t("inventory.outboundReport.value", { defaultValue: "Valor" })}
          value={isDelta ? fmtPct(stats?.value) : fmtMoney(stats?.total_value)}
          delta={isDelta}
          tone={isDelta ? tone(stats?.value) : null}
          icon={isDelta ? arrow(stats?.value) : null}
        />
        <Row
          label={t("inventory.outboundReport.uniqueSkus", { defaultValue: "SKUs" })}
          value={isDelta ? fmtPct(stats?.skus) : fmt(stats?.unique_skus)}
          delta={isDelta}
          tone={isDelta ? tone(stats?.skus) : null}
          icon={isDelta ? arrow(stats?.skus) : null}
        />
        {stats?.from && (
          <div className="pt-1 text-[10px] text-muted-foreground font-mono">
            {stats.from} → {stats.to}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, tone, icon, delta }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span
        className={`font-mono tabular-nums inline-flex items-center gap-1 ${
          delta ? tone || "" : ""
        }`}
      >
        {icon}
        {value}
      </span>
    </div>
  );
}

/** Main component. */
export default function InventoryOutboundReport({
  filters,
  testId = "outbound-report",
}) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metricOrderBy, setMetricOrderBy] = useState("qty");
  const [exporting, setExporting] = useState(false);

  const filterDeps = useMemo(
    () => [
      filters?.preset,
      filters?.date_from,
      filters?.date_to,
      filters?.source_report,
    ],
    [filters?.preset, filters?.date_from, filters?.date_to, filters?.source_report]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    inventory
      .getOutboundReport(filters, { topN: 20, metricOrderBy })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.message || "Error");
          toast.error(
            t("inventory.outboundReport.loadError", {
              defaultValue: "No se pudo cargar el Reporte de Salidas",
            })
          );
          console.error(e);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...filterDeps, metricOrderBy]);

  const activeRanking = useMemo(() => {
    if (!data) return [];
    if (metricOrderBy === "value") return data.ranking_by_value;
    if (metricOrderBy === "orders") return data.ranking_by_frequency;
    return data.ranking_by_units;
  }, [data, metricOrderBy]);

  // Top 10 SKUs for grouped current-vs-previous bars. We use ranking_by_units
  // as the canonical list (qty is the most intuitive).
  const groupedComparison = useMemo(() => {
    if (!data?.ranking_by_units) return [];
    // The endpoint doesn't return per-SKU previous qty (would explode size).
    // We approximate by scaling each SKU proportionally to the period delta —
    // good enough for "shape" comparison. If exact values are needed later
    // we'd add a per-SKU prev field to the API. Documented limitation.
    const cur = data.comparison?.current_period?.total_qty || 0;
    const prev = data.comparison?.previous_period?.total_qty || 0;
    const ratio = cur > 0 ? prev / cur : 0;
    return data.ranking_by_units.slice(0, 10).map((r) => ({
      sku: r.sku,
      vendor: r.vendor,
      actual: r.qty,
      anterior: Math.round(r.qty * ratio),
    }));
  }, [data]);

  // Helper to export the active ranking as CSV (lightweight, no extra dep).
  const handleExportCsv = () => {
    if (!activeRanking?.length) return;
    setExporting(true);
    try {
      const header = ["rank", "sku", "description", "vendor", "qty", "value", "orders", "pct_of_total"];
      const rows = activeRanking.map((r) =>
        header
          .map((h) => {
            const v = r[h];
            if (v == null) return "";
            const s = String(v).replace(/"/g, '""');
            return /[",\n]/.test(s) ? `"${s}"` : s;
          })
          .join(",")
      );
      const csv = [header.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "").replace(/-/g, "");
      a.href = url;
      a.download = `outbound_report_${metricOrderBy}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        t("inventory.outboundReport.exportSuccess", {
          defaultValue: "CSV descargado",
        })
      );
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid={testId}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-32" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card data-testid={`${testId}-error`}>
        <CardContent className="p-6 text-center">
          <Info className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("inventory.outboundReport.loadError", {
              defaultValue: "No se pudo cargar el Reporte de Salidas",
            })}
          </p>
        </CardContent>
      </Card>
    );
  }

  const k = data.kpis;
  const isEmpty = (k?.total_units_out || 0) === 0;

  if (isEmpty) {
    return (
      <Card data-testid={`${testId}-empty`}>
        <CardContent className="p-8 text-center space-y-2">
          <TrendingDown className="mx-auto h-8 w-8 text-muted-foreground" />
          <h3 className="font-semibold">
            {t("inventory.outboundReport.emptyTitle", {
              defaultValue: "Sin salidas en este período",
            })}
          </h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            {t("inventory.outboundReport.emptyDesc", {
              defaultValue:
                "Las salidas reales del stock provienen de IC385DETAIL e IC385P2DETAIL. Ajusta el filtro de Período o fuente para ver datos.",
            })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid={testId}>
      {/* ============ KPIs ============ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <KpiCard
          testId="outbound-kpi-units"
          icon={Package}
          label={t("inventory.outboundReport.kpiUnits", { defaultValue: "Unidades salidas" })}
          value={fmt(k.total_units_out)}
          accent="danger"
        />
        <KpiCard
          testId="outbound-kpi-value"
          icon={DollarSign}
          label={t("inventory.outboundReport.kpiValue", { defaultValue: "Valor $ salido" })}
          value={fmtMoney(k.total_value_out)}
          accent="danger"
        />
        <KpiCard
          testId="outbound-kpi-skus"
          icon={Hash}
          label={t("inventory.outboundReport.kpiSkus", { defaultValue: "SKUs únicos" })}
          value={fmt(k.unique_skus_out)}
        />
        <KpiCard
          testId="outbound-kpi-orders"
          icon={ListChecks}
          label={t("inventory.outboundReport.kpiOrders", { defaultValue: "Órdenes únicas" })}
          value={fmt(k.unique_orders)}
        />
        <KpiCard
          testId="outbound-kpi-top-sku"
          icon={Award}
          label={t("inventory.outboundReport.kpiTopSku", { defaultValue: "Top SKU" })}
          value={k.top_sku?.sku || "—"}
          subLabel={
            k.top_sku
              ? `${fmt(k.top_sku.qty)} u · ${fmtPct(k.top_sku.pct)}`
              : null
          }
          accent="primary"
        />
        <KpiCard
          testId="outbound-kpi-top-vendor"
          icon={Award}
          label={t("inventory.outboundReport.kpiTopVendor", { defaultValue: "Top Vendor" })}
          value={(k.top_vendor?.name || "—").slice(0, 18)}
          subLabel={
            k.top_vendor
              ? `${fmt(k.top_vendor.qty)} u · ${fmtPct(k.top_vendor.pct)}`
              : null
          }
          accent="primary"
        />
      </div>

      {/* ============ Period comparison row ============ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <PeriodCard
          title={t("inventory.outboundReport.currentPeriod", {
            defaultValue: "Período actual",
          })}
          stats={data.comparison?.current_period}
          t={t}
        />
        <PeriodCard
          title={t("inventory.outboundReport.previousPeriod", {
            defaultValue: "Período anterior",
          })}
          stats={data.comparison?.previous_period}
          t={t}
        />
        <PeriodCard
          title={t("inventory.outboundReport.deltaPct", { defaultValue: "Δ %" })}
          stats={data.comparison?.delta_pct}
          isDelta
          t={t}
        />
      </div>

      {/* ============ Selector + Charts 2x2 ============ */}
      <div className="flex items-center justify-between gap-2 pt-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="metric-order-by" className="text-xs">
            {t("inventory.outboundReport.orderTopBy", {
              defaultValue: "Ordenar Top por…",
            })}
          </Label>
          <Select value={metricOrderBy} onValueChange={setMetricOrderBy}>
            <SelectTrigger
              id="metric-order-by"
              className="w-44 h-8 text-xs"
              data-testid="outbound-metric-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="qty" data-testid="metric-qty">
                {t("inventory.outboundReport.metricUnits", { defaultValue: "Unidades" })}
              </SelectItem>
              <SelectItem value="value" data-testid="metric-value">
                {t("inventory.outboundReport.metricValue", { defaultValue: "Valor $" })}
              </SelectItem>
              <SelectItem value="orders" data-testid="metric-orders">
                {t("inventory.outboundReport.metricOrders", {
                  defaultValue: "Frecuencia de órdenes",
                })}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCsv}
          disabled={exporting || !activeRanking?.length}
          data-testid="outbound-export-csv-btn"
        >
          {exporting ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5 mr-1.5" />
          )}
          {t("inventory.outboundReport.exportCsv", { defaultValue: "Exportar CSV" })}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Chart 1 — Horizontal bars Top 20 */}
        <Card data-testid="outbound-chart-ranking">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {t("inventory.outboundReport.chartRanking", {
                defaultValue: "Top 20 SKUs",
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={420}>
              <BarChart
                data={activeRanking.slice(0, 20)}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis
                  type="category"
                  dataKey="sku"
                  width={92}
                  tick={{ fontSize: 10, fontFamily: "monospace" }}
                  interval={0}
                />
                <Tooltip
                  formatter={(v, name) => {
                    if (name === "value") return [fmtMoney(v), "Valor"];
                    if (name === "orders") return [fmt(v), "Órdenes"];
                    return [fmt(v), "Unidades"];
                  }}
                  labelFormatter={(l) => `SKU: ${l}`}
                  contentStyle={{ fontSize: 11 }}
                />
                <Bar dataKey={metricOrderBy === "value" ? "value" : metricOrderBy === "orders" ? "orders" : "qty"} fill="#ef4444" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 2 — Grouped bars current vs previous */}
        <Card data-testid="outbound-chart-comparison">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {t("inventory.outboundReport.chartComparison", {
                defaultValue: "Top 10 — Actual vs Anterior",
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={groupedComparison} margin={{ top: 4, right: 16, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis
                  dataKey="sku"
                  tick={{ fontSize: 9, fontFamily: "monospace" }}
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                  height={50}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(v) => [fmt(v), "Unidades"]}
                  contentStyle={{ fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="actual" fill="#3b82f6" name={t("inventory.outboundReport.current", { defaultValue: "Actual" })} />
                <Bar dataKey="anterior" fill="#94a3b8" name={t("inventory.outboundReport.previous", { defaultValue: "Anterior" })} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 3 — Trend line */}
        <Card data-testid="outbound-chart-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {t("inventory.outboundReport.chartTrend", {
                defaultValue: "Tendencia temporal",
              })}{" "}
              <span className="text-[10px] font-mono uppercase text-muted-foreground">
                ({data.trend_granularity})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={420}>
              <LineChart data={data.trend} margin={{ top: 4, right: 16, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => fmtTrendDate(v, data.trend_granularity)}
                  tick={{ fontSize: 9 }}
                  angle={-30}
                  textAnchor="end"
                  height={50}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  labelFormatter={(v) => fmtTrendDate(v, data.trend_granularity)}
                  formatter={(v) => [fmt(v), "Unidades"]}
                  contentStyle={{ fontSize: 11 }}
                />
                <Line
                  type="monotone"
                  dataKey="qty"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 4 — Pie by vendor */}
        <Card data-testid="outbound-chart-vendor">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {t("inventory.outboundReport.chartVendor", {
                defaultValue: "Distribución por Vendor",
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={420}>
              <PieChart>
                <Pie
                  data={data.by_vendor}
                  dataKey="qty"
                  nameKey="vendor"
                  cx="50%"
                  cy="50%"
                  outerRadius={130}
                  innerRadius={60}
                  paddingAngle={1}
                >
                  {data.by_vendor.map((entry, i) => (
                    <Cell key={entry.vendor} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, name, p) => [
                    `${fmt(v)} u (${fmtPct(p?.payload?.pct)})`,
                    p?.payload?.vendor,
                  ]}
                  contentStyle={{ fontSize: 11 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 10 }}
                  formatter={(value) => value?.slice(0, 22)}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* ============ Pareto full-width ============ */}
      <Card data-testid="outbound-chart-pareto">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {t("inventory.outboundReport.chartPareto", {
              defaultValue: "Pareto — Concentración 80/20",
            })}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={data.pareto} margin={{ top: 4, right: 40, left: 4, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis
                dataKey="sku"
                tick={{ fontSize: 9, fontFamily: "monospace" }}
                angle={-30}
                textAnchor="end"
                interval={0}
                height={50}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 10 }}
                label={{
                  value: "Unidades",
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: 10 },
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 10 }}
                label={{
                  value: "% acum.",
                  angle: 90,
                  position: "insideRight",
                  style: { fontSize: 10 },
                }}
              />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(v, name) => {
                  if (name === "cum_qty_pct") return [`${v}%`, "% acum."];
                  return [fmt(v), "Unidades"];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                yAxisId="left"
                dataKey="qty"
                fill="#3b82f6"
                name={t("inventory.outboundReport.units", { defaultValue: "Unidades" })}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cum_qty_pct"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="% acumulado"
              />
              <ReferenceLine
                yAxisId="right"
                y={80}
                stroke="#ef4444"
                strokeDasharray="4 4"
                label={{ value: "80%", position: "right", fontSize: 10, fill: "#ef4444" }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ============ Detail table ============ */}
      <Card data-testid="outbound-detail-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {t("inventory.outboundReport.tableTitle", {
              defaultValue: "Detalle del Top",
            })}{" "}
            <Badge variant="outline" className="ml-1 text-[10px]">
              {metricOrderBy === "value"
                ? "$"
                : metricOrderBy === "orders"
                ? "Órdenes"
                : "Unidades"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">#</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Unidades</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Órdenes</TableHead>
                  <TableHead className="text-right">% Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeRanking.map((r) => (
                  <TableRow key={r.sku} data-testid={`outbound-row-${r.sku}`}>
                    <TableCell className="font-mono text-xs">{r.rank}</TableCell>
                    <TableCell className="font-mono text-xs">{r.sku}</TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate">
                      {r.description || "—"}
                    </TableCell>
                    <TableCell className="text-xs max-w-[140px] truncate">
                      {r.vendor}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {fmt(r.qty)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {fmtMoney(r.value)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {fmt(r.orders)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {fmtPct(r.pct_of_total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
